import { useEffect, useContext, useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
    Button,
    Grid,
    IconButton,
    Container,
    Paper,
    Typography,
    Card,
    CardContent,
    Stack,
    Box,
    Avatar,
    Chip,
    TextField,
    InputAdornment,
    Tooltip,
    CircularProgress,
    Pagination,
    Popover,
    Divider
} from "@mui/material";
import { collection, query, orderBy, limit, getDocs, where } from "firebase/firestore";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";

// Servicios
import UserContextP from "Interfaces/Context";
import db, { auth, logout } from "Service/Connections";
import {
    RemoteSearchPlan,
    TIPO_PEDIDO_LABEL,
    TipoPedido,
    buildRemoteSearchPlan,
    matchesSearch,
    resolveTipoPedido,
    tokenizeSearch
} from "Pages/UpdateDocument/OrderSearch";
import { resolveCityFromOrder } from "./LocationHelper";

// Iconos
import LogoutIcon from '@mui/icons-material/Logout';
import NoAccountsIcon from '@mui/icons-material/NoAccounts';
import ReportOffIcon from '@mui/icons-material/ReportOff';
import RequestPageIcon from '@mui/icons-material/RequestPage';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import RefreshIcon from '@mui/icons-material/Refresh';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import Loader from "Components/Loader";

type StatusFilterType = 'ALL' | 'PENDING' | 'SAP' | 'CANCELED';
type TipoFilterType = 'ALL' | TipoPedido;

const TIPO_PEDIDO_COLOR: Record<TipoPedido, string> = {
    NORMAL: '#248c4c',
    CONSIGNACION_EVENT: '#e65100',
    CONSIGNACION_PERM: '#4a148c'
};

const matchesStatus = (item: any, statusFilter: StatusFilterType) => {
    if (statusFilter === 'PENDING') return !item.canceled && !item.saveOrderSap;
    if (statusFilter === 'SAP') return Boolean(item.saveOrderSap) && !item.canceled;
    if (statusFilter === 'CANCELED') return Boolean(item.canceled);
    return true;
};

const matchesTipo = (item: any, tipoFilter: TipoFilterType) =>
    tipoFilter === 'ALL' || resolveTipoPedido(item) === tipoFilter;

// Caché en memoria a nivel de módulo para permitir navegación instantánea y sin bloqueos
// al volver desde la vista de detalle de una orden
let cachedPendingOrders: any[] = [];
let cachedRecentOrders: any[] = [];

const AXIOS_TIMEOUT_MS = 12000;

function withTimeout<T>(promise: Promise<T>, ms: number, fallbackValue: T): Promise<T> {
    return Promise.race([
        promise,
        new Promise<T>((resolve) => setTimeout(() => resolve(fallbackValue), ms))
    ]);
}

const formatOrderDoc = (docId: string, data: any) => {
    if (!data || typeof data !== 'object') return null;
    let dateStr = "";
    let rawDate: Date | null = null;
    try {
        if (data.created_at) {
            if (typeof data.created_at.toDate === 'function') {
                rawDate = data.created_at.toDate();
            } else if (data.created_at._seconds) {
                rawDate = new Date(data.created_at._seconds * 1000);
            } else if (typeof data.created_at === 'string' || typeof data.created_at === 'number') {
                rawDate = new Date(data.created_at);
            }
        }
        if (!rawDate && (data.date || data.DocDate)) {
            const candidate = new Date(data.date || data.DocDate);
            if (!isNaN(candidate.getTime())) {
                rawDate = candidate;
            }
        }
        if (rawDate && !isNaN(rawDate.getTime())) {
            dateStr = rawDate.toLocaleDateString('es-ES', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            }) + ', ' + rawDate.toLocaleTimeString('es-ES', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
            });
        }
    } catch (e) {
        console.warn("Error formateando fecha en orden", docId, e);
    }

    let resolvedCity = "";
    try {
        resolvedCity = resolveCityFromOrder(data);
    } catch (e) {
        console.warn("Error resolviendo ciudad en orden", docId, e);
    }

    return {
        ...data,
        $key: docId,
        date: dateStr || data.date || "",
        createdDateObj: rawDate,
        total: data.total ?? data.totalOrder ?? data.DocTotal ?? 0,
        city: resolvedCity || data.city || data.City || ""
    };
};

export const START_DATE_SEPT_1 = new Date(2026, 8, 1, 0, 0, 0); // 01/09/2026 00:00:00

export const getTodayRange = (): { start: Date; end: Date } => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
    const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
    return { start, end };
};

export type DatePresetType =
    | 'TODAY'
    | 'CURRENT_MONTH'
    | 'PREV_MONTH'
    | 'LAST_7'
    | 'LAST_30'
    | 'SEPT_1'
    | 'ALL'
    | 'CUSTOM';

export const getDatePresetRange = (preset: DatePresetType): { start: Date | null; end: Date | null } => {
    const now = new Date();
    switch (preset) {
        case 'TODAY':
            return getTodayRange();
        case 'SEPT_1':
            return { start: START_DATE_SEPT_1, end: null };
        case 'CURRENT_MONTH': {
            const start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
            const end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
            return { start, end };
        }
        case 'PREV_MONTH': {
            const start = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0, 0);
            const end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
            return { start, end };
        }
        case 'LAST_7': {
            const start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            start.setHours(0, 0, 0, 0);
            const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
            return { start, end };
        }
        case 'LAST_30': {
            const start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            start.setHours(0, 0, 0, 0);
            const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
            return { start, end };
        }
        case 'ALL':
            return { start: null, end: null };
        case 'CUSTOM':
        default:
            return { start: START_DATE_SEPT_1, end: null };
    }
};

export const getInitialDateState = (): { preset: DatePresetType; start: Date | null; end: Date | null } => {
    try {
        const savedPreset = sessionStorage.getItem('pt_date_preset') as DatePresetType | null;
        const savedStartStr = sessionStorage.getItem('pt_start_date');
        const savedEndStr = sessionStorage.getItem('pt_end_date');

        if (savedPreset) {
            if (savedPreset === 'CUSTOM') {
                const start = savedStartStr ? new Date(savedStartStr) : null;
                const end = savedEndStr ? new Date(savedEndStr) : null;
                const validStart = start && !isNaN(start.getTime()) ? start : null;
                const validEnd = end && !isNaN(end.getTime()) ? end : null;
                if (validStart || validEnd) {
                    return { preset: 'CUSTOM', start: validStart, end: validEnd };
                }
            } else if (
                savedPreset === 'SEPT_1' ||
                savedPreset === 'TODAY' ||
                savedPreset === 'CURRENT_MONTH' ||
                savedPreset === 'PREV_MONTH' ||
                savedPreset === 'LAST_7' ||
                savedPreset === 'LAST_30' ||
                savedPreset === 'ALL'
            ) {
                const range = getDatePresetRange(savedPreset);
                return { preset: savedPreset, start: range.start, end: range.end };
            }
        }
    } catch {}

    // Por defecto: 'SEPT_1' (Desde 01/09/2026), mostrando todos los pedidos pendientes y del historial
    return {
        preset: 'SEPT_1',
        start: START_DATE_SEPT_1,
        end: null
    };
};

export const toDateInputValue = (date: Date | null): string => {
    if (!date || isNaN(date.getTime())) return "";
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

export const parseDateInput = (val: string, endOfDay = false): Date | null => {
    if (!val) return null;
    const parts = val.split('-');
    if (parts.length !== 3) return null;
    const y = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10) - 1;
    const d = parseInt(parts[2], 10);
    if (isNaN(y) || isNaN(m) || isNaN(d)) return null;
    if (endOfDay) {
        return new Date(y, m, d, 23, 59, 59, 999);
    }
    return new Date(y, m, d, 0, 0, 0, 0);
};

export const matchesDateRange = (item: any, start: Date | null, end: Date | null): boolean => {
    const d = item.createdDateObj;
    if (!d) return true;
    if (start && d.getTime() < start.getTime()) return false;
    if (end && d.getTime() > end.getTime()) return false;
    return true;
};

const REMOTE_SEARCH_LIMIT = 25;

// Busca en todo el historial: N° de orden exacto y prefijo de código, cliente o vendedor
const searchOrdersRemote = async (plan: RemoteSearchPlan) => {
    const ordersRef = collection(db, "orders");
    const byPrefix = (field: string, value: string) =>
        query(ordersRef, where(field, ">=", value), where(field, "<=", value + "\uf8ff"), limit(REMOTE_SEARCH_LIMIT));

    const queries = [byPrefix("cardCode", plan.codePrefix)];
    plan.namePrefixes.forEach((prefix) => queries.push(byPrefix("cardName", prefix), byPrefix("vendorName", prefix)));
    if (plan.numberOrder !== null) {
        queries.push(query(ordersRef, where("numberOrder", "==", plan.numberOrder), limit(REMOTE_SEARCH_LIMIT)));
        queries.push(query(ordersRef, where("numberOrder", "==", String(plan.numberOrder)), limit(REMOTE_SEARCH_LIMIT)));
    }

    const results = await Promise.allSettled(queries.map((q) => getDocs(q)));
    const found: any[] = [];
    results.forEach((result) => {
        if (result.status === "fulfilled") {
            result.value.docs.forEach((docSnap) => {
                const formatted = formatOrderDoc(docSnap.id, docSnap.data());
                if (formatted) found.push(formatted);
            });
        } else {
            console.warn("Fallo búsqueda en historial:", result.reason);
        }
    });
    return found;
};

const ListDocument = () => {
    const context = useContext(UserContextP);
    const navigate = useNavigate();
    const [errorLogin, setErrorLogin] = useState(false);
    const [errorServer, setErrorServer] = useState(false);
    const [pendingOrders, setPendingOrders] = useState<any[]>(() => cachedPendingOrders);
    const [recentOrders, setRecentOrders] = useState<any[]>(() => cachedRecentOrders);
    const hasInitialData = cachedPendingOrders.length > 0 || cachedRecentOrders.length > 0;
    const [loader, setLoader] = useState(() => !hasInitialData);
    const [isSyncing, setIsSyncing] = useState(false);
    const isFetchingOrdersRef = useRef(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState<StatusFilterType>(() => {
        try {
            const saved = sessionStorage.getItem('pt_status_filter');
            if (saved === 'ALL' || saved === 'PENDING' || saved === 'SAP' || saved === 'CANCELED') {
                return saved as StatusFilterType;
            }
        } catch {}
        return 'ALL';
    });

    const handleStatusFilterChange = (newStatus: StatusFilterType) => {
        setStatusFilter(newStatus);
        try {
            sessionStorage.setItem('pt_status_filter', newStatus);
        } catch {}
    };

    // Filtros de fechas calendario (por defecto 'SEPT_1' / Desde 01/09/2026 para mostrar todos los pendientes, persistido en sesión)
    const initialDateConfig = useMemo(() => getInitialDateState(), []);
    const [datePreset, setDatePreset] = useState<DatePresetType>(initialDateConfig.preset);
    const [startDate, setStartDate] = useState<Date | null>(initialDateConfig.start);
    const [endDate, setEndDate] = useState<Date | null>(initialDateConfig.end);

    // Estado temporal para Popover de fechas
    const [tempStartDate, setTempStartDate] = useState<Date | null>(initialDateConfig.start);
    const [tempEndDate, setTempEndDate] = useState<Date | null>(initialDateConfig.end);
    const [selectionStep, setSelectionStep] = useState<'START' | 'END'>('START');
    const [calendarAnchorEl, setCalendarAnchorEl] = useState<HTMLElement | null>(null);
    const isCalendarOpen = Boolean(calendarAnchorEl);

    const [tipoFilter, setTipoFilter] = useState<TipoFilterType>('ALL');
    const [remoteResults, setRemoteResults] = useState<any[]>([]);
    const [searchingRemote, setSearchingRemote] = useState(false);
    const [firestoreSearchEnabled, setFirestoreSearchEnabled] = useState(false);
    const [firestoreError, setFirestoreError] = useState(false);
    const [page, setPage] = useState(1);
    const ITEMS_PER_PAGE = 10;

    const searchTokens = useMemo(() => tokenizeSearch(searchTerm), [searchTerm]);
    const isSearching = searchTokens.length > 0;
    // Los filtros por estado aplican en todo momento tanto al historial como a las búsquedas
    const activeStatus: StatusFilterType = statusFilter;
    const loadedKeys = useMemo(
        () => new Set([...pendingOrders, ...recentOrders].map((item: any) => item.$key)),
        [pendingOrders, recentOrders]
    );

    // Fuente unificada: muestra todas las órdenes disponibles según el rango de fechas y búsqueda
    const matchedOrders = useMemo(() => {
        const source = [...pendingOrders, ...recentOrders, ...remoteResults];
        const unique = new Map<string, any>();
        source.forEach((item: any) => {
            const key = item.$key || item.id;
            if (!key) return;
            if (!matchesDateRange(item, startDate, endDate)) return;
            if (!matchesSearch(item, searchTokens)) return;
            if (!unique.has(key)) unique.set(key, item);
        });
        return Array.from(unique.values()).sort(
            (a: any, b: any) => (b.createdDateObj?.getTime() ?? 0) - (a.createdDateObj?.getTime() ?? 0)
        );
    }, [pendingOrders, recentOrders, remoteResults, searchTokens, startDate, endDate]);

    // Contadores reales por estado, respetando el tipo de pedido seleccionado
    const counts = useMemo(() => {
        let all = 0;
        let pending = 0;
        let sap = 0;
        let canceled = 0;
        matchedOrders.forEach((item: any) => {
            if (!matchesTipo(item, tipoFilter)) return;
            all++;
            if (item.canceled) canceled++;
            else if (item.saveOrderSap) sap++;
            else pending++;
        });
        return { all, pending, sap, canceled };
    }, [matchedOrders, tipoFilter]);

    // Contadores reales por tipo de pedido, respetando el estado seleccionado
    const tipoCounts = useMemo(() => {
        const result: Record<TipoPedido, number> = { NORMAL: 0, CONSIGNACION_EVENT: 0, CONSIGNACION_PERM: 0 };
        matchedOrders.forEach((item: any) => {
            if (matchesStatus(item, activeStatus)) result[resolveTipoPedido(item)]++;
        });
        return result;
    }, [matchedOrders, activeStatus]);

    const filteredOrders = useMemo(
        () => matchedOrders.filter((item: any) => matchesStatus(item, activeStatus) && matchesTipo(item, tipoFilter)),
        [matchedOrders, activeStatus, tipoFilter]
    );

    const totalPages = useMemo(() => {
        return Math.ceil(filteredOrders.length / ITEMS_PER_PAGE) || 1;
    }, [filteredOrders.length]);

    const paginatedOrders = useMemo(() => {
        const startIndex = (page - 1) * ITEMS_PER_PAGE;
        return filteredOrders.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [filteredOrders, page]);

    // Resetea a la primera página si cambian los criterios de búsqueda o filtros
    useEffect(() => {
        setPage(1);
    }, [searchTerm, statusFilter, tipoFilter, startDate, endDate]);

    const historyMatches = useMemo(
        () => filteredOrders.filter((item: any) => !loadedKeys.has(item.$key)).length,
        [filteredOrders, loadedKeys]
    );

    // Las órdenes cargadas son solo las más recientes; el término también se busca en todo el historial
    useEffect(() => {
        const plan = firestoreSearchEnabled ? buildRemoteSearchPlan(searchTerm) : null;
        if (!plan) {
            setRemoteResults([]);
            setSearchingRemote(false);
            return;
        }

        let active = true;
        setSearchingRemote(true);
        const timer = setTimeout(async () => {
            const results = await searchOrdersRemote(plan);
            if (!active) return;
            setRemoteResults(results);
            setSearchingRemote(false);
        }, 350);

        return () => {
            active = false;
            clearTimeout(timer);
        };
    }, [searchTerm, firestoreSearchEnabled]);

    // Función de carga inicial de autenticación
    useEffect(() => {
        document.title = "Órdenes - Premier Tools";
        if (!context.user.id || !context.authenticate.uid || !context.authenticate.token) {
            const unsubscribe = auth.onAuthStateChanged(async (response) => {
                if (response !== null) {
                    try {
                        const res = await axios.get(`${process.env.REACT_APP_URL_ORDER}/api/v1/auth/generate/token`, {
                            headers: {
                                Authorization: `UserId ${response.uid}`
                            },
                            timeout: AXIOS_TIMEOUT_MS
                        });
                        context.assignUser(
                            response.uid,
                            res.data?.data?.user?.last_name ?? "",
                            res.data?.data?.user?.name ?? "",
                            res.data?.data?.user?.email ?? ""
                        );
                        context.assignateAuth(response.uid ?? "", true, res.data?.data?.token ?? "");
                    } catch {
                        navigate("/auth/signin");
                    }
                } else {
                    navigate("/auth/signin");
                }
            });
            return () => {
                if (typeof unsubscribe === "function") unsubscribe();
            };
        }
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (context.user.id !== "" && context.authenticate.uid !== "" && Boolean(context.authenticate.token)) {
            getDataOrder();
        }
        // eslint-disable-next-line
    }, [context.user.id, context.authenticate.uid]);

    // Función auxiliar para obtener un token fresco usando Firebase Auth
    const fetchFreshToken = async (): Promise<string> => {
        const uid = auth.currentUser?.uid || context.authenticate.uid || context.user.id;
        if (!uid) return "";
        try {
            const res = await axios.get(`${process.env.REACT_APP_URL_ORDER}/api/v1/auth/generate/token`, {
                headers: { Authorization: `UserId ${uid}` },
                timeout: AXIOS_TIMEOUT_MS
            });
            const newToken = res.data?.data?.token ?? "";
            if (newToken) {
                context.assignateAuth(uid, true, newToken);
                return newToken;
            }
        } catch (err) {
            console.warn("No se pudo renovar token de autorización:", err);
        }
        return "";
    };

    // Carga órdenes pendientes desde la API y el historial de Firestore según el rango de fechas
    const getDataOrder = async (customStart?: Date | null, customEnd?: Date | null, forceFullLoader = false) => {
        if (isFetchingOrdersRef.current) return;
        isFetchingOrdersRef.current = true;

        const hasAnyCached = cachedPendingOrders.length > 0 || cachedRecentOrders.length > 0;
        if (forceFullLoader || !hasAnyCached) {
            setLoader(true);
        } else {
            setIsSyncing(true);
        }

        setErrorServer(false);
        setErrorLogin(false);
        setFirestoreError(false);

        // Safety timer para asegurar que NUNCA se quede congelado el loader bajo ninguna circunstancia
        const safetyTimeout = setTimeout(() => {
            if (isFetchingOrdersRef.current) {
                console.warn("Safety timer de órdenes activado tras 15s sin respuesta.");
                setLoader(false);
                setIsSyncing(false);
                isFetchingOrdersRef.current = false;
            }
        }, 15000);

        try {
            let activeToken = context.authenticate.token;
            if (!activeToken) {
                activeToken = await fetchFreshToken();
            }

            const effectiveStart = customStart !== undefined ? customStart : startDate;
            const effectiveEnd = customEnd !== undefined ? customEnd : endDate;

            const fetchFirestoreHistory = async () => {
                const ordersRef = collection(db, "orders");
                try {
                    let q;
                    if (effectiveStart && effectiveEnd) {
                        q = query(
                            ordersRef,
                            where("created_at", ">=", effectiveStart),
                            where("created_at", "<=", effectiveEnd),
                            orderBy("created_at", "desc"),
                            limit(1000)
                        );
                    } else if (effectiveStart) {
                        q = query(
                            ordersRef,
                            where("created_at", ">=", effectiveStart),
                            orderBy("created_at", "desc"),
                            limit(1000)
                        );
                    } else {
                        q = query(ordersRef, orderBy("created_at", "desc"), limit(500));
                    }

                    const snap = await withTimeout(getDocs(q), 10000, null);
                    if (!snap) throw new Error("Firestore query timeout");
                    return snap.docs
                        .map((docSnap) => formatOrderDoc(docSnap.id, docSnap.data()))
                        .filter(Boolean);
                } catch (err1: any) {
                    console.warn("Fallo consulta con filtro de fechas, intentando fallback:", err1);
                    try {
                        const qFallback = query(ordersRef, orderBy("created_at", "desc"), limit(500));
                        const snapFallback = await withTimeout(getDocs(qFallback), 8000, null);
                        if (!snapFallback) return [];
                        return snapFallback.docs
                            .map((docSnap) => formatOrderDoc(docSnap.id, docSnap.data()))
                            .filter(Boolean)
                            .filter((item: any) => matchesDateRange(item, effectiveStart, effectiveEnd));
                    } catch (fallbackErr) {
                        console.warn("Fallo completo de Firestore:", fallbackErr);
                        return [];
                    }
                }
            };

            const fetchPendingOrders = async (tokenToUse: string): Promise<any[]> => {
                if (!tokenToUse) return [];
                try {
                    const res = await axios.get(`${process.env.REACT_APP_URL_ORDER}/api/v1/orders/pending`, {
                        headers: {
                            Authorization: `Bearer ${tokenToUse}`
                        },
                        params: {
                            canceled: false
                        },
                        timeout: AXIOS_TIMEOUT_MS
                    });
                    return (res.data?.data || [])
                        .map((item: any) => formatOrderDoc(item?.$key || item?.id, item))
                        .filter(Boolean);
                } catch (err: any) {
                    console.warn("Fallo petición a orders/pending, intentando renovar token:", err);
                    const refreshed = await fetchFreshToken();
                    if (refreshed && refreshed !== tokenToUse) {
                        const retryRes = await axios.get(`${process.env.REACT_APP_URL_ORDER}/api/v1/orders/pending`, {
                            headers: {
                                Authorization: `Bearer ${refreshed}`
                            },
                            params: {
                                canceled: false
                            },
                            timeout: AXIOS_TIMEOUT_MS
                        });
                        return (retryRes.data?.data || [])
                            .map((item: any) => formatOrderDoc(item?.$key || item?.id, item))
                            .filter(Boolean);
                    }
                    throw err;
                }
            };

            const [pendingResult, recentResult] = await Promise.allSettled([
                fetchPendingOrders(activeToken),
                fetchFirestoreHistory()
            ]);

            let hasLoadedFirestore = false;
            if (recentResult.status === "fulfilled") {
                setRecentOrders(recentResult.value);
                cachedRecentOrders = recentResult.value;
                setFirestoreSearchEnabled(true);
                setFirestoreError(false);
                hasLoadedFirestore = recentResult.value.length > 0;
            } else {
                console.warn("Sin lectura de órdenes en Firestore; verificar reglas de seguridad:", recentResult.reason);
                if (cachedRecentOrders.length === 0) {
                    setRecentOrders([]);
                }
                setFirestoreSearchEnabled(false);
                setFirestoreError(true);
            }

            if (pendingResult.status === "fulfilled") {
                setPendingOrders(pendingResult.value);
                cachedPendingOrders = pendingResult.value;
                setErrorServer(false);
                setErrorLogin(false);
            } else {
                console.warn("No se pudieron cargar órdenes pendientes desde la API:", pendingResult.reason);
                const status = pendingResult.reason?.response?.status;
                if (hasLoadedFirestore || cachedPendingOrders.length > 0) {
                    setErrorServer(false);
                    setErrorLogin(false);
                } else {
                    setErrorLogin(status === 403 || status === 401);
                    setErrorServer(status !== 403 && status !== 401);
                }
            }
        } catch (globalErr) {
            console.error("Error global en getDataOrder:", globalErr);
            if (cachedPendingOrders.length === 0 && cachedRecentOrders.length === 0) {
                setErrorServer(true);
            }
        } finally {
            clearTimeout(safetyTimeout);
            setLoader(false);
            setIsSyncing(false);
            isFetchingOrdersRef.current = false;
        }
    };

    // Cambia de página suavemente
    const handlePageChange = (newPage: number) => {
        setPage(newPage);
        window.scrollTo({ top: 220, behavior: 'smooth' });
    };

    const applyDateFilter = (start: Date | null, end: Date | null, preset: DatePresetType) => {
        setStartDate(start);
        setEndDate(end);
        setDatePreset(preset);
        setCalendarAnchorEl(null);
        setPage(1);
        try {
            sessionStorage.setItem('pt_date_preset', preset);
            if (start) {
                sessionStorage.setItem('pt_start_date', start.toISOString());
            } else {
                sessionStorage.removeItem('pt_start_date');
            }
            if (end) {
                sessionStorage.setItem('pt_end_date', end.toISOString());
            } else {
                sessionStorage.removeItem('pt_end_date');
            }
        } catch {}
        getDataOrder(start, end);
    };

    const handleOpenCalendar = (e: React.MouseEvent<HTMLElement>) => {
        setTempStartDate(startDate);
        setTempEndDate(endDate);
        setSelectionStep('START');
        setCalendarAnchorEl(e.currentTarget);
    };

    const handleSelectPreset = (preset: DatePresetType) => {
        const range = getDatePresetRange(preset);
        setTempStartDate(range.start);
        setTempEndDate(range.end);
        setSelectionStep('START');
        applyDateFilter(range.start, range.end, preset);
    };

    const handleDayClick = (clickedDay: Date) => {
        if (selectionStep === 'START') {
            const s = new Date(clickedDay);
            s.setHours(0, 0, 0, 0);
            setTempStartDate(s);
            setTempEndDate(null);
            setSelectionStep('END');
            setDatePreset('CUSTOM');
        } else {
            // Seleccionando Hasta
            const clicked = new Date(clickedDay);
            if (tempStartDate && clicked.getTime() < tempStartDate.getTime()) {
                // El valor del campo Desde no podrá ser mayor que el campo Hasta: intercambiar para ordenar
                const newStart = new Date(clicked);
                newStart.setHours(0, 0, 0, 0);
                const newEnd = new Date(tempStartDate);
                newEnd.setHours(23, 59, 59, 999);
                setTempStartDate(newStart);
                setTempEndDate(newEnd);
            } else {
                const e = new Date(clicked);
                e.setHours(23, 59, 59, 999);
                setTempEndDate(e);
            }
            setSelectionStep('START');
            setDatePreset('CUSTOM');
        }
    };

    const getTileClassName = ({ date, view }: { date: Date; view: string }) => {
        if (view !== 'month') return '';
        const time = date.getTime();
        const dStr = toDateInputValue(date);
        const startStr = toDateInputValue(tempStartDate);
        const endStr = toDateInputValue(tempEndDate);

        if (startStr && dStr === startStr && endStr && dStr === endStr) {
            return 'react-calendar__tile--active react-calendar__tile--rangeStart react-calendar__tile--rangeEnd';
        }
        if (startStr && dStr === startStr) {
            return 'react-calendar__tile--rangeStart react-calendar__tile--active';
        }
        if (endStr && dStr === endStr) {
            return 'react-calendar__tile--rangeEnd react-calendar__tile--active';
        }
        if (tempStartDate && tempEndDate) {
            const s = new Date(tempStartDate).setHours(0, 0, 0, 0);
            const e = new Date(tempEndDate).setHours(23, 59, 59, 999);
            if (time > s && time < e) {
                return 'react-calendar__tile--range';
            }
        }
        return '';
    };

    const handleApplyCustomDates = () => {
        let finalStart = tempStartDate;
        let finalEnd = tempEndDate;

        if (finalStart && finalEnd && finalStart.getTime() > finalEnd.getTime()) {
            const temp = finalStart;
            finalStart = finalEnd;
            finalEnd = temp;
        }

        if (finalStart && !finalEnd) {
            finalEnd = new Date(finalStart);
            finalEnd.setHours(23, 59, 59, 999);
        }

        if (!finalStart && finalEnd) {
            finalStart = new Date(finalEnd);
            finalStart.setHours(0, 0, 0, 0);
        }

        if (!finalStart && !finalEnd) {
            const defaultRange = getDatePresetRange('SEPT_1');
            finalStart = defaultRange.start;
            finalEnd = defaultRange.end;
        }

        applyDateFilter(finalStart, finalEnd, 'CUSTOM');
    };

    const handleResetDates = () => {
        try {
            sessionStorage.removeItem('pt_date_preset');
            sessionStorage.removeItem('pt_start_date');
            sessionStorage.removeItem('pt_end_date');
        } catch {}
        handleSelectPreset('SEPT_1');
    };

    const dateRangeLabel = useMemo(() => {
        const formatD = (d: Date) => d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
        if (datePreset === 'SEPT_1') return "📅 Desde 01/09/2026";
        if (datePreset === 'TODAY') {
            const todayStr = formatD(new Date());
            return `📅 Hoy (${todayStr})`;
        }
        if (datePreset === 'CURRENT_MONTH') {
            const monthName = new Date().toLocaleDateString('es-ES', { month: 'long' });
            return `📅 Mes actual (${monthName.charAt(0).toUpperCase() + monthName.slice(1)})`;
        }
        if (datePreset === 'PREV_MONTH') {
            const prev = new Date();
            prev.setMonth(prev.getMonth() - 1);
            const monthName = prev.toLocaleDateString('es-ES', { month: 'long' });
            return `📅 ${monthName.charAt(0).toUpperCase() + monthName.slice(1)}`;
        }
        if (datePreset === 'LAST_7') return "📅 Últimos 7 días";
        if (datePreset === 'LAST_30') return "📅 Últimos 30 días";
        if (datePreset === 'ALL') return "📅 Todo el historial";
        if (startDate && endDate) {
            const s = formatD(startDate);
            const e = formatD(endDate);
            return s === e ? `📅 ${s}` : `📅 ${s} - ${e}`;
        }
        if (startDate) {
            return `📅 Desde ${formatD(startDate)}`;
        }
        if (endDate) {
            return `📅 Hasta ${formatD(endDate)}`;
        }
        return "📅 Filtrar fechas";
    }, [datePreset, startDate, endDate]);

    // Método para navegar al detalle de la orden
    const navigateTo = (item: any) => {
        context.assignateCopyData({ order: item });
        navigate(item.$key);
    };

    const closeSession = async () => {
        try {
            sessionStorage.removeItem('pt_status_filter');
            sessionStorage.removeItem('pt_date_preset');
            sessionStorage.removeItem('pt_start_date');
            sessionStorage.removeItem('pt_end_date');
        } catch {}
        cachedPendingOrders = [];
        cachedRecentOrders = [];
        context.assignUser("", "", "", "");
        context.assignateAuth("", false, "");
        await logout();
        navigate("/auth/signin");
    };

    const resetFilters = () => {
        setSearchTerm("");
        setStatusFilter('ALL');
        try {
            sessionStorage.removeItem('pt_status_filter');
            sessionStorage.removeItem('pt_date_preset');
            sessionStorage.removeItem('pt_start_date');
            sessionStorage.removeItem('pt_end_date');
        } catch {}
        setTipoFilter('ALL');
        setPage(1);
        handleSelectPreset('SEPT_1');
    };

    return (
        <Container maxWidth="lg" sx={{ py: 3 }}>
            {loader ? (
                <Loader openLoader={loader} inPage={true} redirect={false} />
            ) : errorServer ? (
                <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3, maxWidth: 520, mx: 'auto' }}>
                    <ReportOffIcon sx={{ fontSize: 90, color: 'error.main' }} />
                    <Typography variant="h5" sx={{ mt: 2, fontWeight: 700, color: '#112a56' }}>
                        No se pudieron cargar las órdenes
                    </Typography>
                    <Typography sx={{ mt: 1, mb: 3, color: 'text.secondary', fontSize: '0.95rem' }}>
                        Hubo una dificultad de conexión con el servidor. Por favor, verifica tu conexión o intenta nuevamente.
                    </Typography>
                    <Stack direction="row" spacing={2} justifyContent="center">
                        <Button variant="contained" onClick={() => getDataOrder(undefined, undefined, true)} sx={{ borderRadius: '50px', textTransform: 'none', px: 3, fontWeight: 600 }}>
                            Reintentar
                        </Button>
                        <Button variant="outlined" onClick={closeSession} sx={{ borderRadius: '50px', textTransform: 'none', px: 2.5 }}>
                            Cerrar sesión
                        </Button>
                    </Stack>
                </Paper>
            ) : errorLogin ? (
                <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3, maxWidth: 520, mx: 'auto' }}>
                    <NoAccountsIcon sx={{ fontSize: 90, color: 'warning.main' }} />
                    <Typography variant="h5" sx={{ mt: 2, fontWeight: 700, color: '#112a56' }}>
                        Acceso no autorizado o sesión expirada
                    </Typography>
                    <Typography sx={{ mt: 1, mb: 3, color: 'text.secondary', fontSize: '0.95rem' }}>
                        Tu sesión ha caducado o tu usuario no cuenta con permisos administrativos para revisar órdenes. Por favor inicia sesión nuevamente.
                    </Typography>
                    <Button variant="contained" sx={{ borderRadius: '50px', textTransform: 'none', px: 3, fontWeight: 600 }} onClick={closeSession}>
                        Iniciar sesión de nuevo
                    </Button>
                </Paper>
            ) : (
                <Paper sx={{ p: { xs: 2, md: 4 }, borderRadius: 3, boxShadow: '0 4px 20px rgba(17, 42, 86, 0.05)' }}>
                    {/* Header y Acciones */}
                    <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                        <Tooltip title={isSyncing ? "Sincronizando órdenes..." : "Actualizar lista"}>
                            <span>
                                <IconButton
                                    disabled={isSyncing}
                                    onClick={() => getDataOrder(undefined, undefined, true)}
                                    aria-label="Actualizar órdenes"
                                    sx={{
                                        color: '#112a56',
                                        transition: 'transform 0.3s ease',
                                        animation: isSyncing ? 'spin 1s linear infinite' : 'none',
                                        '@keyframes spin': {
                                            '0%': { transform: 'rotate(0deg)' },
                                            '100%': { transform: 'rotate(360deg)' }
                                        }
                                    }}
                                >
                                    <RefreshIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title="Cerrar sesión">
                            <IconButton onClick={closeSession} aria-label="Cerrar sesión" sx={{ color: '#64748b' }}>
                                <LogoutIcon />
                            </IconButton>
                        </Tooltip>
                    </Stack>

                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center" sx={{ mb: 2 }}>
                        <Box component="img" src={require('Media/Logo.png')} alt="Logo premier tools" sx={{ height: 110 }} />
                        <Box>
                            <Typography variant="h4" sx={{ color: '#112a56', fontWeight: 800 }}>
                                Órdenes y Documentos
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                                Hola <strong>{context.user.name || "Usuario"}</strong>, consulta el historial general de movimientos y gestiona las órdenes pendientes de autorizar.
                            </Typography>
                        </Box>
                    </Stack>

                    {/* Aviso si Firestore no tiene permisos de lectura */}
                    {firestoreError && (
                        <Box sx={{ maxWidth: 700, mx: 'auto', mb: 2, p: 1.5, bgcolor: '#fffbeb', borderRadius: 2, border: '1px solid #fef3c7', textAlign: 'center' }}>
                            <Typography variant="body2" sx={{ color: '#92400e', fontSize: '0.85rem' }}>
                                ℹ️ <strong>Historial de Firestore:</strong> Para visualizar las órdenes históricas en SAP y canceladas, activa la regla de lectura en Firebase Console (<code>allow read: if request.auth != null;</code>).
                            </Typography>
                        </Box>
                    )}

                    {/* Buscador y Botón de Filtrar por Fechas a la par */}
                    <Box sx={{ maxWidth: 780, mx: 'auto', mt: 3, mb: 1.5 }}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center">
                            <TextField
                                fullWidth
                                placeholder="Buscar por cliente, código (ej: CR0001), vendedor, N° de orden o tipo de pedido..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start" sx={{ ml: 0.5 }}>
                                            <SearchIcon sx={{ color: '#9aa0a6', fontSize: 24 }} />
                                        </InputAdornment>
                                    ),
                                    endAdornment: searchTerm ? (
                                        <InputAdornment position="end" sx={{ mr: 0.5 }}>
                                            {searchingRemote && <CircularProgress size={18} sx={{ color: '#9aa0a6', mr: 1 }} />}
                                            <IconButton size="small" onClick={() => setSearchTerm("")}>
                                                 <ClearIcon fontSize="small" sx={{ color: '#70757a' }} />
                                            </IconButton>
                                        </InputAdornment>
                                    ) : null,
                                    sx: {
                                        borderRadius: '50px',
                                        bgcolor: '#ffffff',
                                        height: 50,
                                        px: 1.5,
                                        boxShadow: '0 2px 8px rgba(32, 33, 36, 0.12)',
                                        '& fieldset': {
                                            borderColor: '#dfe1e5',
                                            borderRadius: '50px',
                                            transition: 'all 0.2s ease'
                                        },
                                        '&:hover fieldset': {
                                            borderColor: '#cbd5e1'
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#112a56',
                                            borderWidth: '1.5px'
                                        },
                                        '& input': {
                                            py: 1,
                                            fontSize: '0.95rem',
                                            color: '#202124'
                                        }
                                    }
                                }}
                            />

                            <Tooltip title="Filtrar por fechas calendario o períodos rápidos">
                                <Button
                                    variant="outlined"
                                    onClick={handleOpenCalendar}
                                    startIcon={<CalendarMonthIcon sx={{ color: '#112a56' }} />}
                                    sx={{
                                        height: 50,
                                        borderRadius: '50px',
                                        px: 2.5,
                                        whiteSpace: 'nowrap',
                                        textTransform: 'none',
                                        fontWeight: 700,
                                        fontSize: '0.88rem',
                                        bgcolor: '#ffffff',
                                        color: '#112a56',
                                        borderColor: '#dfe1e5',
                                        boxShadow: '0 2px 8px rgba(32, 33, 36, 0.12)',
                                        transition: 'all 0.2s ease',
                                        flexShrink: 0,
                                        '&:hover': {
                                            bgcolor: '#f8fafc',
                                            borderColor: '#112a56',
                                            boxShadow: '0 4px 12px rgba(17, 42, 86, 0.12)'
                                        }
                                    }}
                                >
                                    {dateRangeLabel}
                                </Button>
                            </Tooltip>
                        </Stack>

                        {/* Barra de Filtros por Estado (siempre activa) */}
                        <Stack
                            direction="row"
                            spacing={1}
                            justifyContent="center"
                            alignItems="center"
                            flexWrap="wrap"
                            sx={{ mt: 2, gap: 1 }}
                        >
                            <Chip
                                icon={<LayersOutlinedIcon sx={{ fontSize: 16 }} />}
                                label={`Todas (${counts.all})`}
                                clickable
                                color={statusFilter === 'ALL' ? 'primary' : 'default'}
                                variant={statusFilter === 'ALL' ? 'filled' : 'outlined'}
                                onClick={() => handleStatusFilterChange('ALL')}
                                sx={{
                                    borderRadius: '50px',
                                    fontWeight: 700,
                                    bgcolor: statusFilter === 'ALL' ? '#112a56' : '#f8fafc',
                                    borderColor: '#cbd5e1'
                                }}
                            />
                            <Chip
                                icon={<HourglassTopIcon sx={{ fontSize: 16, color: '#d97706 !important' }} />}
                                label={`Pendientes (${counts.pending})`}
                                clickable
                                variant={statusFilter === 'PENDING' ? 'filled' : 'outlined'}
                                onClick={() => handleStatusFilterChange('PENDING')}
                                sx={{
                                    borderRadius: '50px',
                                    fontWeight: 700,
                                    bgcolor: statusFilter === 'PENDING' ? '#fef3c7' : '#ffffff',
                                    borderColor: '#fde68a',
                                    color: '#92400e'
                                }}
                            />
                            <Chip
                                icon={<CheckCircleOutlineIcon sx={{ fontSize: 16, color: '#16a34a !important' }} />}
                                label={`En SAP (${counts.sap})`}
                                clickable
                                variant={statusFilter === 'SAP' ? 'filled' : 'outlined'}
                                onClick={() => handleStatusFilterChange('SAP')}
                                sx={{
                                    borderRadius: '50px',
                                    fontWeight: 700,
                                    bgcolor: statusFilter === 'SAP' ? '#dcfce7' : '#ffffff',
                                    borderColor: '#bbf7d0',
                                    color: '#166534'
                                }}
                            />
                            <Chip
                                icon={<CancelOutlinedIcon sx={{ fontSize: 16, color: '#dc2626 !important' }} />}
                                label={`Canceladas (${counts.canceled})`}
                                clickable
                                variant={statusFilter === 'CANCELED' ? 'filled' : 'outlined'}
                                onClick={() => handleStatusFilterChange('CANCELED')}
                                sx={{
                                    borderRadius: '50px',
                                    fontWeight: 700,
                                    bgcolor: statusFilter === 'CANCELED' ? '#fee2e2' : '#ffffff',
                                    borderColor: '#fecaca',
                                    color: '#991b1b'
                                }}
                            />
                        </Stack>

                        {/* Popover con Calendario Interactivo y Opciones Rápidas */}
                        <Popover
                            open={isCalendarOpen}
                            anchorEl={calendarAnchorEl}
                            onClose={() => setCalendarAnchorEl(null)}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'center',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'center',
                            }}
                            PaperProps={{
                                sx: {
                                    p: 2.5,
                                    width: { xs: '92vw', sm: 390 },
                                    maxWidth: 410,
                                    borderRadius: 3,
                                    boxShadow: '0 12px 36px rgba(17, 42, 86, 0.16)',
                                    border: '1px solid #e2e8f0'
                                }
                            }}
                        >
                            <Stack spacing={2}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <CalendarMonthIcon sx={{ color: '#112a56', fontSize: 22 }} />
                                        <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#112a56' }}>
                                            Filtrar por Fechas
                                        </Typography>
                                    </Stack>
                                    <IconButton size="small" onClick={() => setCalendarAnchorEl(null)}>
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                </Stack>

                                <Divider />

                                {/* Opciones Rápidas */}
                                <Box>
                                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, display: 'block', mb: 1 }}>
                                        Períodos rápidos:
                                    </Typography>
                                    <Stack direction="row" spacing={0.8} flexWrap="wrap" sx={{ gap: 0.8 }}>
                                        {[
                                            { id: 'TODAY', label: 'Hoy' },
                                            { id: 'CURRENT_MONTH', label: 'Mes actual' },
                                            { id: 'PREV_MONTH', label: 'Mes anterior' },
                                            { id: 'LAST_7', label: 'Últimos 7 días' },
                                            { id: 'LAST_30', label: 'Últimos 30 días' },
                                            { id: 'SEPT_1', label: 'Desde 01/09/2026' },
                                            { id: 'ALL', label: 'Todo el historial' },
                                        ].map((p) => {
                                            const isSelected = datePreset === p.id;
                                            return (
                                                <Chip
                                                    key={p.id}
                                                    label={p.label}
                                                    size="small"
                                                    clickable
                                                    variant={isSelected ? 'filled' : 'outlined'}
                                                    onClick={() => handleSelectPreset(p.id as DatePresetType)}
                                                    sx={{
                                                        fontWeight: 700,
                                                        fontSize: '11px',
                                                        borderRadius: '50px',
                                                        bgcolor: isSelected ? '#112a56' : '#ffffff',
                                                        color: isSelected ? '#ffffff' : '#334155',
                                                        borderColor: isSelected ? '#112a56' : '#cbd5e1'
                                                    }}
                                                />
                                            );
                                        })}
                                    </Stack>
                                </Box>

                                {/* Indicador del paso de selección */}
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#f8fafc', px: 1.5, py: 0.8, borderRadius: 2, border: '1px solid #e2e8f0' }}>
                                    <Typography variant="caption" sx={{ color: '#475569', fontWeight: 600 }}>
                                        {selectionStep === 'START' ? '1. Haz clic en el calendario para fecha Desde' : '2. Haz clic en el calendario para fecha Hasta'}
                                    </Typography>
                                    <Chip
                                        label={selectionStep === 'START' ? 'Paso: Desde' : 'Paso: Hasta'}
                                        size="small"
                                        sx={{
                                            fontSize: '10px',
                                            height: 20,
                                            fontWeight: 700,
                                            bgcolor: selectionStep === 'START' ? '#dbeafe' : '#fef3c7',
                                            color: selectionStep === 'START' ? '#1e40af' : '#92400e'
                                        }}
                                    />
                                </Box>

                                {/* Calendario mensual interactivo */}
                                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                    <Calendar
                                        className="pt-calendar-custom"
                                        selectRange={false}
                                        onClickDay={handleDayClick}
                                        tileClassName={getTileClassName}
                                        value={tempStartDate || new Date()}
                                    />
                                </Box>

                                {/* Inputs manuales de Desde y Hasta */}
                                <Grid container spacing={1.5}>
                                    <Grid item xs={6}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Desde"
                                            type="date"
                                            value={toDateInputValue(tempStartDate)}
                                            onFocus={() => setSelectionStep('START')}
                                            onChange={(e) => {
                                                const d = parseDateInput(e.target.value, false);
                                                if (d && tempEndDate && d.getTime() > tempEndDate.getTime()) {
                                                    setTempStartDate(d);
                                                    const newEnd = new Date(d);
                                                    newEnd.setHours(23, 59, 59, 999);
                                                    setTempEndDate(newEnd);
                                                } else {
                                                    setTempStartDate(d);
                                                }
                                                setSelectionStep('END');
                                                setDatePreset('CUSTOM');
                                            }}
                                            inputProps={{
                                                max: tempEndDate ? toDateInputValue(tempEndDate) : undefined
                                            }}
                                            InputLabelProps={{ shrink: true }}
                                            sx={{ '& input': { fontSize: '12px', py: 1 } }}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Hasta"
                                            type="date"
                                            value={toDateInputValue(tempEndDate)}
                                            onFocus={() => setSelectionStep('END')}
                                            onChange={(e) => {
                                                const d = parseDateInput(e.target.value, true);
                                                if (d && tempStartDate && d.getTime() < tempStartDate.getTime()) {
                                                    setTempEndDate(d);
                                                    const newStart = new Date(d);
                                                    newStart.setHours(0, 0, 0, 0);
                                                    setTempStartDate(newStart);
                                                } else {
                                                    setTempEndDate(d);
                                                }
                                                setSelectionStep('START');
                                                setDatePreset('CUSTOM');
                                            }}
                                            inputProps={{
                                                min: tempStartDate ? toDateInputValue(tempStartDate) : undefined
                                            }}
                                            InputLabelProps={{ shrink: true }}
                                            sx={{ '& input': { fontSize: '12px', py: 1 } }}
                                        />
                                    </Grid>
                                </Grid>

                                {/* Botones de acción */}
                                <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ pt: 1 }}>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={handleResetDates}
                                        sx={{ borderRadius: '50px', textTransform: 'none', fontWeight: 600, color: '#475569', borderColor: '#cbd5e1' }}
                                    >
                                        Restablecer
                                    </Button>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={handleApplyCustomDates}
                                        sx={{ borderRadius: '50px', textTransform: 'none', fontWeight: 700, bgcolor: '#112a56', '&:hover': { bgcolor: '#0f2747' } }}
                                    >
                                        Aplicar fechas
                                    </Button>
                                </Stack>
                            </Stack>
                        </Popover>

                        {/* Filtro por tipo de pedido (Orden Normal / Consignación) */}
                        <Stack
                            direction="row"
                            spacing={1}
                            justifyContent="center"
                            alignItems="center"
                            flexWrap="wrap"
                            sx={{ mt: 1, gap: 1 }}
                        >
                            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700 }}>
                                Tipo de pedido:
                            </Typography>
                            {(Object.keys(TIPO_PEDIDO_LABEL) as TipoPedido[]).map((tipo) => {
                                const selected = tipoFilter === tipo;
                                return (
                                    <Chip
                                        key={tipo}
                                        label={`${TIPO_PEDIDO_LABEL[tipo]} (${tipoCounts[tipo]})`}
                                        clickable
                                        variant={selected ? 'filled' : 'outlined'}
                                        onClick={() => setTipoFilter(selected ? 'ALL' : tipo)}
                                        sx={{
                                            borderRadius: '50px',
                                            fontWeight: 700,
                                            borderColor: TIPO_PEDIDO_COLOR[tipo],
                                            color: selected ? '#ffffff' : TIPO_PEDIDO_COLOR[tipo],
                                            bgcolor: selected ? TIPO_PEDIDO_COLOR[tipo] : '#ffffff',
                                            '&:hover': { bgcolor: selected ? TIPO_PEDIDO_COLOR[tipo] : '#f8fafc' }
                                        }}
                                    />
                                );
                            })}
                        </Stack>

                        {/* Contador y feedback de resultados */}
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ mt: 1.5 }}>
                            <Typography variant="caption" color="text.secondary">
                                {isSearching ? (
                                    <>
                                        <strong>{filteredOrders.length}</strong> resultado{filteredOrders.length === 1 ? '' : 's'}
                                        {historyMatches > 0 && ` (${historyMatches} del historial)`}
                                    </>
                                ) : (
                                    <>
                                        <strong>{filteredOrders.length}</strong> {filteredOrders.length === 1 ? 'orden' : 'órdenes'} {
                                            activeStatus === 'PENDING' ? 'pendientes de autorizar' :
                                            activeStatus === 'SAP' ? 'registradas en SAP' :
                                            activeStatus === 'CANCELED' ? 'canceladas' :
                                            'en total'
                                        }
                                        {totalPages > 1 && ` • Página ${page} de ${totalPages}`}
                                    </>
                                )}
                                {searchingRemote && " • ¡Buscando en el historial!..."}
                                {` • ${dateRangeLabel}`}
                                {tipoFilter !== 'ALL' && ` • Tipo: ${TIPO_PEDIDO_LABEL[tipoFilter]}`}
                            </Typography>
                            {(searchTerm || activeStatus !== 'ALL' || datePreset !== 'SEPT_1' || tipoFilter !== 'ALL') && (
                                <Button
                                    size="small"
                                    onClick={resetFilters}
                                    sx={{ textTransform: "none", py: 0, px: 1, minHeight: 0, fontSize: "11px", fontWeight: 700, color: '#112a56' }}
                                >
                                    Restablecer
                                </Button>
                            )}
                        </Stack>
                    </Box>

                    {/* Lista de Órdenes o Spinner 'Buscando en el historial' */}
                    {searchingRemote ? (
                        <Paper
                            elevation={0}
                            sx={{
                                py: 7,
                                px: 3,
                                my: 2,
                                textAlign: 'center',
                                borderRadius: 3,
                                bgcolor: '#f8fafc',
                                border: '1.5px dashed #cbd5e1'
                            }}
                        >
                            <Box sx={{ position: 'relative', display: 'inline-flex', mb: 2 }}>
                                <CircularProgress size={48} thickness={4.5} sx={{ color: '#112a56' }} />
                            </Box>
                            <Typography variant="h5" sx={{ color: '#112a56', fontWeight: 800 }}>
                                ¡Estamos Obteniendo los Datos!
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontSize: '0.95rem' }}>
                                Buscando en el historial de órdenes para "{searchTerm}"...
                            </Typography>
                        </Paper>
                    ) : filteredOrders.length > 0 ? (
                        <>
                            <Stack spacing={2}>
                                {paginatedOrders.map((item: any, index: number) => {
                                const deliveryDate = item.dateDelivery || item.deliveryDate || item.docDueDate || item.DueDate;
                                const tipoPedido = resolveTipoPedido(item);

                                return (
                                    <Card
                                        key={item.$key ?? index}
                                        variant="outlined"
                                        onClick={() => navigateTo(item)}
                                        sx={{
                                            borderRadius: 2.5,
                                            border: '1px solid #e2e8f0',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            '&:hover': {
                                                borderColor: '#112a56',
                                                boxShadow: '0 6px 20px rgba(17, 42, 86, 0.08)',
                                                transform: 'translateY(-2px)',
                                                '& .chevron-icon': {
                                                    color: '#112a56',
                                                    transform: 'translateX(4px)'
                                                }
                                            }
                                        }}
                                    >
                                        <CardContent sx={{ p: { xs: 2, md: 2.5 }, '&:last-child': { pb: { xs: 2, md: 2.5 } } }}>
                                            <Grid container spacing={1.5} alignItems="center">
                                                {/* Fila Superior: Avatar + Cliente / Orden de venta (izq) y Badges + PPL + Total (der) */}
                                                <Grid item xs={12}>
                                                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'flex-start' }, gap: 1.5 }}>
                                                        {/* Lado izquierdo: Avatar verde + Encabezado */}
                                                        <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ minWidth: 0, flex: 1 }}>
                                                            <Avatar sx={{ bgcolor: '#198754', color: '#ffffff', width: 46, height: 46, borderRadius: '50%', mt: 0.3, boxShadow: '0 2px 6px rgba(25, 135, 84, 0.25)' }}>
                                                                <RequestPageIcon fontSize="small" />
                                                            </Avatar>
                                                            <Box sx={{ minWidth: 0 }}>
                                                                {/* Línea: Badge #Orden + Nombre + (Código) */}
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                                    <Typography
                                                                        sx={{
                                                                            bgcolor: '#0f2747',
                                                                            color: '#ffffff',
                                                                            px: 1.2,
                                                                            py: 0.3,
                                                                            borderRadius: '50px',
                                                                            fontWeight: 800,
                                                                            fontSize: '12px',
                                                                            letterSpacing: 0.3
                                                                        }}
                                                                    >
                                                                        #{item.numberOrder ?? "—"}
                                                                    </Typography>
                                                                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0f2747', fontSize: '1.05rem', lineHeight: 1.3 }}>
                                                                        {item.cardName || "CLIENTE SIN NOMBRE"}
                                                                    </Typography>
                                                                    {item.cardCode && (
                                                                        <Typography component="span" sx={{ color: '#64748b', fontWeight: 600, fontSize: '0.95rem' }}>
                                                                            ({item.cardCode})
                                                                        </Typography>
                                                                    )}
                                                                </Box>

                                                                {/* Subtítulo: Orden de venta / Orden de compra */}
                                                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.4 }}>
                                                                    <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>
                                                                        {item.type === 'PurchaseOrder' ? 'Orden de compra' : 'Orden de venta'}
                                                                    </Typography>
                                                                    {/* Estado si está cancelada o ya en SAP */}
                                                                    {item.canceled ? (
                                                                        <Chip label="Cancelada" size="small" sx={{ bgcolor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', fontWeight: 700, fontSize: '10px', height: 20 }} />
                                                                    ) : item.saveOrderSap ? (
                                                                        <Chip label="En SAP" size="small" sx={{ bgcolor: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0', fontWeight: 700, fontSize: '10px', height: 20 }} />
                                                                    ) : null}
                                                                    {/* Tipo de pedido: solo se marca cuando es consignación */}
                                                                    {tipoPedido !== 'NORMAL' && (
                                                                        <Chip
                                                                            label={TIPO_PEDIDO_LABEL[tipoPedido]}
                                                                            size="small"
                                                                            sx={{ bgcolor: TIPO_PEDIDO_COLOR[tipoPedido], color: '#ffffff', fontWeight: 700, fontSize: '10px', height: 20 }}
                                                                        />
                                                                    )}
                                                                </Stack>
                                                            </Box>
                                                        </Stack>

                                                        {/* Lado derecho: Badges originales + PPL + Total */}
                                                        <Box sx={{ alignSelf: { xs: 'flex-start', md: 'flex-end' }, textAlign: { xs: 'left', md: 'right' }, minWidth: { md: 280 } }}>
                                                            {/* Fila de Insignias: Con descuento, Crédito Fiscal, Condición de Pago */}
                                                            <Stack direction="row" spacing={1} alignItems="center" justifyContent={{ xs: 'flex-start', md: 'flex-end' }} flexWrap="wrap" sx={{ gap: 0.6 }}>
                                                                {/* Con descuento */}
                                                                {(Number(item.discount ?? 0) > 0 || Number(item.descuento ?? 0) > 0) && (
                                                                    <Chip
                                                                        icon={<LocalOfferIcon sx={{ fontSize: '13px !important', color: '#ffffff !important' }} />}
                                                                        label="Con descuento"
                                                                        size="small"
                                                                        sx={{
                                                                            bgcolor: '#207435',
                                                                            color: '#ffffff',
                                                                            fontWeight: 700,
                                                                            fontSize: '11px',
                                                                            height: 24,
                                                                            borderRadius: '50px',
                                                                            px: 0.5,
                                                                            boxShadow: '0 1px 3px rgba(32, 116, 53, 0.2)'
                                                                        }}
                                                                    />
                                                                )}

                                                                {/* Tipo de Documento: Crédito Fiscal / Factura */}
                                                                <Chip
                                                                    label={
                                                                        item.typeDocument === 'CRF' || item.facSerie === 'CCF'
                                                                            ? 'Crédito Fiscal'
                                                                            : item.typeDocument === 'COF' || item.facSerie === 'FAC'
                                                                            ? 'Factura'
                                                                            : (item.typeDocument || 'Crédito Fiscal')
                                                                    }
                                                                    size="small"
                                                                    sx={{
                                                                        bgcolor: '#ffffff',
                                                                        border: '1.5px solid #dbeafe',
                                                                        color: '#1e293b',
                                                                        fontWeight: 700,
                                                                        fontSize: '11px',
                                                                        height: 24,
                                                                        borderRadius: '50px',
                                                                        px: 0.5
                                                                    }}
                                                                />

                                                                {/* Condición de Pago: CREDITO 30 DÍAS */}
                                                                {item.typePayment && (
                                                                    <Chip
                                                                        label={String(item.typePayment).toUpperCase()}
                                                                        size="small"
                                                                        sx={{
                                                                            bgcolor: '#f0fdf4',
                                                                            border: '1.5px solid #86efac',
                                                                            color: '#15803d',
                                                                            fontWeight: 700,
                                                                            fontSize: '11px',
                                                                            height: 24,
                                                                            borderRadius: '50px',
                                                                            px: 0.5
                                                                        }}
                                                                    />
                                                                )}
                                                            </Stack>

                                                            {/* PPL / Código de Sucursal y Ciudad */}
                                                            {(item.payToCode || item.address || item.city) && (
                                                                <Typography
                                                                    sx={{
                                                                        color: '#8c959f',
                                                                        fontSize: '14px',
                                                                        fontWeight: 500,
                                                                        textAlign: { xs: 'left', md: 'right' },
                                                                        letterSpacing: 0.5,
                                                                        mt: 0.5,
                                                                        pr: 0.5
                                                                    }}
                                                                >
                                                                    {typeof item.payToCode === 'string'
                                                                        ? item.payToCode
                                                                        : (item.payToCode?.location || item.address || '')}
                                                                    {item.city && (
                                                                        <Typography component="span" sx={{ color: '#047857', fontWeight: 700, ml: 0.8, fontSize: '13px' }}>
                                                                            • {item.city}
                                                                        </Typography>
                                                                    )}
                                                                </Typography>
                                                            )}

                                                            {/* Total: 317.98 */}
                                                            <Typography
                                                                variant="h5"
                                                                sx={{
                                                                    fontWeight: 800,
                                                                    color: '#0f2747',
                                                                    textAlign: { xs: 'left', md: 'right' },
                                                                    mt: 0.4,
                                                                    lineHeight: 1.2
                                                                }}
                                                            >
                                                                Total: {Number(item.total || 0).toFixed(2)}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </Grid>

                                                {/* Fila Inferior: Solicitada por, Hora y Chevron */}
                                                <Grid item xs={12}>
                                                    <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center" sx={{ pt: 1, borderTop: '1px solid #f1f5f9' }}>
                                                        <Typography variant="body2" color="text.secondary">
                                                            Solicitada por: <strong>{item.vendorName || "-"}</strong>
                                                            {item.date && ` • Hora: ${item.date}`}
                                                            {deliveryDate && (
                                                                <Typography component="span" sx={{ ml: 1, color: '#0284c7', fontWeight: 600, fontSize: '12px' }}>
                                                                    (Entrega: {String(deliveryDate).split('T')[0]})
                                                                </Typography>
                                                            )}
                                                        </Typography>
                                                        <ChevronRightIcon
                                                            className="chevron-icon"
                                                            sx={{ color: '#94a3b8', transition: 'all 0.2s ease' }}
                                                        />
                                                    </Stack>
                                                </Grid>
                                            </Grid>
                                        </CardContent>
                                    </Card>
                                );
                            })}
                        </Stack>

                        {/* Controles de Paginación (10 en 10) */}
                        {totalPages > 1 && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 2,
                                    mt: 3,
                                    pt: 2.5,
                                    borderTop: '1px solid #f1f5f9'
                                }}
                            >
                                <Typography variant="body2" color="text.secondary">
                                    {isSearching ? (
                                        <>Mostrando <strong>{((page - 1) * ITEMS_PER_PAGE) + 1}</strong> - <strong>{Math.min(page * ITEMS_PER_PAGE, filteredOrders.length)}</strong> de <strong>{filteredOrders.length}</strong> órdenes</>
                                    ) : (
                                        <>Página <strong>{page}</strong> de <strong>{totalPages}</strong> (<strong>10</strong> por página • <strong>{filteredOrders.length}</strong> {
                                            activeStatus === 'PENDING' ? 'pendientes' :
                                            activeStatus === 'SAP' ? 'en SAP' :
                                            activeStatus === 'CANCELED' ? 'canceladas' :
                                            'en total'
                                        })</>
                                    )}
                                </Typography>
                                <Pagination
                                    count={totalPages}
                                    page={page}
                                    onChange={(_, value) => {
                                        handlePageChange(value);
                                    }}
                                    disabled={searchingRemote}
                                    color="primary"
                                    shape="rounded"
                                    showFirstButton
                                    showLastButton
                                    siblingCount={1}
                                    boundaryCount={1}
                                    sx={{
                                        '& .MuiPaginationItem-root': {
                                            fontWeight: 600
                                        },
                                        '& .Mui-selected': {
                                            bgcolor: '#112a56 !important',
                                            color: '#ffffff'
                                        }
                                    }}
                                />
                            </Box>
                        )}
                        </>
                    ) : !isSearching && tipoFilter === 'ALL' && activeStatus === 'PENDING' ? (
                        <Box sx={{ textAlign: 'center', py: 6 }}>
                            <RequestPageIcon sx={{ color: '#fc9803', fontSize: 90 }} />
                            <Typography variant="h5" sx={{ mt: 2, fontWeight: 700, color: '#112a56' }}>
                                No hay órdenes pendientes de autorizar
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                Puedes cambiar de filtro (Todas, En SAP, Canceladas) o consultar fechas anteriores en el calendario.
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{ textAlign: 'center', py: 6 }}>
                            <RequestPageIcon sx={{ color: '#fc9803', fontSize: 90 }} />
                            <Typography variant="h5" sx={{ mt: 2, fontWeight: 700, color: '#112a56' }}>
                                No se encontraron órdenes
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                No hay resultados con los filtros y término de búsqueda seleccionados.
                            </Typography>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={resetFilters}
                                sx={{ mt: 2, textTransform: "none", borderRadius: '50px', borderColor: '#112a56', color: '#112a56' }}
                            >
                                Limpiar filtros
                            </Button>
                        </Box>
                    )}
                </Paper>
            )}
        </Container>
    );
};

export default ListDocument;